import {
  Directory,
  Filesystem,
  type FilesystemPlugin,
} from "@capacitor/filesystem";

import {
  gameplayConfig,
  type SkinConfig,
  type SnakeVisualConfig,
} from "../config/gameplay-config";
import type { ShareImagePayload } from "../config/share-config";
import {
  createSnakeHeadOutline,
  projectSnakeHeadFeature,
} from "../config/snake-head-geometry";

export interface NativeResultImagePort {
  create(image: ShareImagePayload): Promise<string>;
}

export const nativeResultImageSize = {
  height: 1350,
  width: 1080,
} as const;
const pngPrefix = "data:image/png;base64,";

const cssColor = (color: number): string =>
  `#${color.toString(16).padStart(6, "0")}`;

export interface ResultSnakePoint {
  readonly x: number;
  readonly y: number;
}

export interface ResultSnakeSection extends ResultSnakePoint {
  readonly radius: number;
}

export interface ResultSnakeGeometry {
  readonly bodyRadius: number;
  readonly centerline: readonly ResultSnakePoint[];
  readonly headCenter: ResultSnakePoint;
  readonly headTip: ResultSnakePoint;
  readonly neck: ResultSnakeSection;
  readonly outline: readonly [ResultSnakePoint, ...ResultSnakePoint[]];
  readonly patternMarks: readonly ResultSnakeSection[];
  readonly sections: readonly ResultSnakeSection[];
  readonly tailTip: ResultSnakePoint;
}

const shareSnakeCenterY = 760;
const shareSnakeHorizontalOffset = 147;
const shareSnakeHeadCenterX = 142 + shareSnakeHorizontalOffset;
const shareSnakeBodyRadius = 56;

const traceRoundedRectangle = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  const cornerRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + cornerRadius, y);
  context.lineTo(x + width - cornerRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + cornerRadius);
  context.lineTo(x + width, y + height - cornerRadius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - cornerRadius,
    y + height,
  );
  context.lineTo(x + cornerRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - cornerRadius);
  context.lineTo(x, y + cornerRadius);
  context.quadraticCurveTo(x, y, x + cornerRadius, y);
  context.closePath();
};

export const createResultSnakeGeometry = (
  visual: SnakeVisualConfig = gameplayConfig.snakeVisual,
): ResultSnakeGeometry => {
  const headCenter = {
    x: shareSnakeHeadCenterX,
    y: shareSnakeCenterY,
  };
  const headTip = {
    x: headCenter.x - shareSnakeBodyRadius * visual.headLengthScale,
    y: shareSnakeCenterY,
  };
  const direction = {
    forwardX: -1,
    forwardY: 0,
    sideX: 0,
    sideY: -1,
  } as const;
  const neckCenter = projectSnakeHeadFeature(
    headCenter,
    shareSnakeBodyRadius,
    direction,
    -0.62,
    0,
  );
  const neck = {
    radius: shareSnakeBodyRadius * visual.neckRadiusScale,
    x: neckCenter.x,
    y: shareSnakeCenterY,
  };
  const sections: readonly ResultSnakeSection[] = [
    neck,
    {
      radius: 52 * visual.bodyWidthScale,
      x: 250 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
    {
      radius: 50 * visual.bodyWidthScale,
      x: 340 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
    {
      radius: 47 * visual.bodyWidthScale,
      x: 430 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
    {
      radius: 43 * visual.bodyWidthScale,
      x: 520 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
    {
      radius: 40 * visual.bodyWidthScale,
      x: 605 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
    {
      radius: shareSnakeBodyRadius * visual.tailBaseScale,
      x: 675 + shareSnakeHorizontalOffset,
      y: shareSnakeCenterY,
    },
  ];
  const tailTip = {
    x:
      675 +
      shareSnakeHorizontalOffset +
      shareSnakeBodyRadius * visual.tailPointScale,
    y: shareSnakeCenterY,
  };
  const headOutline = createSnakeHeadOutline(
    headCenter,
    shareSnakeBodyRadius,
    direction,
    visual,
  );
  const bodySections = sections.slice(1);
  const lowerOutline = bodySections.map((section) => ({
    x: section.x,
    y: section.y + section.radius,
  }));
  const upperOutline = [...bodySections].reverse().map((section) => ({
    x: section.x,
    y: section.y - section.radius,
  }));
  const outline: [ResultSnakePoint, ...ResultSnakePoint[]] = [
    headOutline[0],
    ...headOutline.slice(1),
    ...lowerOutline,
    tailTip,
    ...upperOutline,
    headOutline[0],
  ];
  return {
    bodyRadius: shareSnakeBodyRadius,
    centerline: [headTip, headCenter, ...sections, tailTip],
    headCenter,
    headTip,
    neck,
    outline,
    patternMarks: sections.slice(1, -1),
    sections,
    tailTip,
  };
};

const fillCircle = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
): void => {
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.fill();
};

const fillTriangle = (
  context: CanvasRenderingContext2D,
  first: ResultSnakePoint,
  second: ResultSnakePoint,
  third: ResultSnakePoint,
): void => {
  context.beginPath();
  context.moveTo(first.x, first.y);
  context.lineTo(second.x, second.y);
  context.lineTo(third.x, third.y);
  context.closePath();
  context.fill();
};

const renderPatternMark = (
  context: CanvasRenderingContext2D,
  mark: ResultSnakeSection,
  index: number,
  pattern: SkinConfig["pattern"],
  bodyColor: string,
  accentColor: string,
): void => {
  const side = index % 2 === 0 ? -1 : 1;
  context.fillStyle = accentColor;
  if (pattern === "leaf") {
    fillTriangle(
      context,
      { x: mark.x - mark.radius * 0.42, y: mark.y },
      {
        x: mark.x + mark.radius * 0.28,
        y: mark.y - mark.radius * 0.34,
      },
      {
        x: mark.x + mark.radius * 0.28,
        y: mark.y + mark.radius * 0.34,
      },
    );
    return;
  }
  if (pattern === "spots") {
    fillCircle(
      context,
      mark.x,
      mark.y + side * mark.radius * 0.28,
      mark.radius * 0.2,
    );
    return;
  }
  if (pattern === "stripes") {
    context.fillRect(
      mark.x - mark.radius * 0.12,
      mark.y - mark.radius * 0.78,
      mark.radius * 0.24,
      mark.radius * 1.56,
    );
    return;
  }
  if (pattern === "ripples" || pattern === "loops") {
    fillCircle(context, mark.x, mark.y, mark.radius * 0.4);
    context.fillStyle = bodyColor;
    fillCircle(context, mark.x, mark.y, mark.radius * 0.25);
    return;
  }
  if (pattern === "confetti") {
    context.fillRect(
      mark.x - mark.radius * 0.28,
      mark.y + side * mark.radius * 0.18,
      mark.radius * 0.56,
      mark.radius * 0.18,
    );
    return;
  }
  if (pattern === "freckles") {
    fillCircle(
      context,
      mark.x - mark.radius * 0.18,
      mark.y + side * mark.radius * 0.25,
      mark.radius * 0.1,
    );
    fillCircle(
      context,
      mark.x + mark.radius * 0.2,
      mark.y - side * mark.radius * 0.12,
      mark.radius * 0.07,
    );
    return;
  }
  fillCircle(context, mark.x, mark.y, mark.radius * 0.42);
  context.fillStyle = bodyColor;
  fillCircle(context, mark.x + mark.radius * 0.2, mark.y, mark.radius * 0.38);
};

const renderFace = (
  context: CanvasRenderingContext2D,
  geometry: ResultSnakeGeometry,
  visual: SnakeVisualConfig,
): void => {
  const eyeX =
    geometry.headCenter.x - geometry.bodyRadius * visual.eyeForwardScale;
  const highlightX =
    geometry.headCenter.x -
    geometry.bodyRadius *
      (visual.eyeForwardScale + visual.eyeHighlightForwardScale);
  const eyeRadius = geometry.bodyRadius * visual.eyeRadiusScale;
  const highlightRadius = geometry.bodyRadius * visual.eyeHighlightRadiusScale;
  for (const side of [-1, 1]) {
    const eyeY =
      geometry.headCenter.y + side * geometry.bodyRadius * visual.eyeSideScale;
    context.fillStyle = cssColor(visual.outlineColor);
    fillCircle(context, eyeX, eyeY, eyeRadius);
    context.fillStyle = "#ffffff";
    fillCircle(context, highlightX, eyeY, highlightRadius);
  }
  const mouthX =
    geometry.headCenter.x - geometry.bodyRadius * visual.mouthForwardScale;
  context.fillStyle = cssColor(visual.outlineColor);
  fillTriangle(
    context,
    { x: mouthX - 5, y: geometry.headCenter.y },
    { x: mouthX + 7, y: geometry.headCenter.y - 4 },
    { x: mouthX + 7, y: geometry.headCenter.y + 4 },
  );
};

const renderSnake = (
  context: CanvasRenderingContext2D,
  image: ShareImagePayload,
): void => {
  const visual = gameplayConfig.snakeVisual;
  const geometry = createResultSnakeGeometry(visual);
  const bodyColor = cssColor(image.bodyColor);
  const accentColor = cssColor(image.accentColor);
  context.beginPath();
  const [firstPoint, ...remainingPoints] = geometry.outline;
  context.moveTo(firstPoint.x, firstPoint.y);
  for (const point of remainingPoints) {
    context.lineTo(point.x, point.y);
  }
  context.closePath();
  context.fillStyle = bodyColor;
  context.fill();
  context.lineJoin = "round";
  context.lineWidth = Math.max(
    6,
    geometry.bodyRadius * visual.outlineWidthScale,
  );
  context.strokeStyle = cssColor(visual.outlineColor);
  context.stroke();
  context.save();
  context.clip();
  for (const [index, mark] of geometry.patternMarks.entries()) {
    renderPatternMark(
      context,
      mark,
      index,
      image.pattern,
      bodyColor,
      accentColor,
    );
  }
  context.restore();
  renderFace(context, geometry, visual);
};

export const createResultImageDataUrl = (
  image: ShareImagePayload,
  documentScope: Document,
): string => {
  const canvas = documentScope.createElement("canvas");
  canvas.width = nativeResultImageSize.width;
  canvas.height = nativeResultImageSize.height;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("Result image canvas is unavailable");
  }
  const background = context.createLinearGradient(
    0,
    0,
    nativeResultImageSize.width,
    nativeResultImageSize.height,
  );
  background.addColorStop(0, "#0d5c63");
  background.addColorStop(1, "#31295f");
  context.fillStyle = background;
  context.fillRect(
    0,
    0,
    nativeResultImageSize.width,
    nativeResultImageSize.height,
  );
  traceRoundedRectangle(context, 54, 54, 972, 1242, 56);
  context.fillStyle = "rgba(6, 38, 52, 0.88)";
  context.fill();
  context.lineJoin = "round";
  context.lineWidth = 6;
  context.strokeStyle = "#d9fbe5";
  context.stroke();
  context.textAlign = "center";
  context.fillStyle = "#d9fbe5";
  context.font = "900 42px system-ui, sans-serif";
  context.fillText("SNAKELY", 540, 150, 840);
  context.fillStyle = "#ffe89a";
  context.fillRect(420, 190, 240, 6);
  context.fillStyle = "#ffe89a";
  context.font = "800 40px system-ui, sans-serif";
  context.fillText(image.resultTag, 540, 280, 840);
  context.fillStyle = "#ffffff";
  context.font = "900 110px system-ui, sans-serif";
  context.fillText(image.headline, 540, 430, 840);
  context.fillStyle = "#d9fbe5";
  context.font = "700 44px system-ui, sans-serif";
  context.fillText(image.modeLabel, 540, 520, 840);
  renderSnake(context, image);
  traceRoundedRectangle(context, 310, 880, 460, 92, 46);
  context.fillStyle = "#173f4f";
  context.fill();
  context.fillStyle = "#ffffff";
  context.font = "700 36px system-ui, sans-serif";
  context.fillText(image.skinName, 540, 940, 380);
  context.fillStyle = "#d9fbe5";
  context.font = "700 36px system-ui, sans-serif";
  context.fillText("Slither. Eat. Grow.", 540, 1160, 720);
  const dataUrl = canvas.toDataURL("image/png");
  if (!dataUrl.startsWith(pngPrefix) || dataUrl.length === pngPrefix.length) {
    throw new Error("Result image PNG is invalid");
  }
  return dataUrl;
};

export class NativeResultImage implements NativeResultImagePort {
  public constructor(
    private readonly filesystem: Pick<
      FilesystemPlugin,
      "writeFile"
    > = Filesystem,
    private readonly documentScope?: Document,
  ) {}

  public async create(image: ShareImagePayload): Promise<string> {
    const dataUrl = createResultImageDataUrl(
      image,
      this.documentScope ?? document,
    );
    const result = await this.filesystem.writeFile({
      data: dataUrl.slice(pngPrefix.length),
      directory: Directory.Cache,
      path: "snakely-result.png",
    });
    return result.uri;
  }
}
